import { useMemo } from 'react'

import {
  type InfiniteData,
  type QueryClient,
  type QueryKey,
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import {
  dedupeFeedEntries,
  findCachedFeedEntryReadState,
  readStateUnreadCountDelta,
  statusChangeUnreadCountDelta,
  updateFeedEntriesBookmarkedPageData,
  updateFeedEntriesPageData,
  updateFeedEntriesReadPageData,
  updateFeedEntryDetail,
  updateFeedEntryDetailBookmarked,
  updateFeedEntryDetailRead,
  updateMonitorListUnreadCount,
  updateMonitorUnreadCount,
} from '@/hooks/monitors/feedCache'
import {
  EMPTY_FEED_STATUS_COUNTS,
  EMPTY_FEED_TYPE_COUNTS,
  MONITOR_FEED_PAGE_SIZE,
  type MonitorFeedListResponse,
  type MonitorListResponse,
  type MonitorRecipeList,
  type MonitorSlackChannelCreate,
  type OrgFeedQuery,
  batchSetMonitorFeedEntryRead,
  batchSetMonitorFeedEntryStatus,
  createMonitor,
  createMonitorSlackChannel,
  deleteMonitor,
  deleteMonitorSlackChannel,
  fetchMonitor,
  fetchMonitorFeedEntry,
  fetchMonitorFeedEntryLineage,
  fetchMonitorRecipes,
  fetchMonitorSlackChannels,
  fetchOrganizationFeed,
  fetchOrganizationMonitors,
  setMonitorFeedEntryBookmarked,
  setMonitorFeedEntryRead,
  updateMonitor,
  updateMonitorFeedEntryStatus,
} from '@/hooks/monitors/fetchers'
import { buildOwnerMap, toDashboardMonitor } from '@/hooks/monitors/mappers'
import {
  type OrgFeedListFilter,
  monitorsQueryKeys,
  orgFeedListFilterFromKey,
} from '@/hooks/monitors/types'
import { useOrganizationUsers } from '@/hooks/organization/useOrganizationUsers'

import type { PaginateRequest } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type {
  CatalogRecipe,
  MonitorCreate,
  MonitorFeedEntryDetailOut,
  MonitorFeedEntryOut,
  MonitorFeedEntryStatus,
  MonitorOverviewOut,
  MonitorUpdate,
} from '@/types/Monitor'

const MONITOR_OVERVIEW_REFETCH_INTERVAL_MS = 30_000
const MONITOR_RECIPES_STALE_TIME_MS = 5 * 60_000

// Org-feed freshness peek cadence. Feed entries land via a once-a-minute
// Temporal ingest drain, so a ~60s page-1 poll is the right cadence — anything
// tighter just re-fetches the same page. The peek is a cheap fixed-size first
// page (newest-by-time) used ONLY to detect arrivals not yet in the loaded set.
const FEED_FRESHNESS_POLL_MS = 60_000
export const FEED_FRESHNESS_PEEK_SIZE = 20

// ─── Recipe catalog (live) ───────────────────────────────────────────────────

const selectRecipeList = (list: MonitorRecipeList): CatalogRecipe[] => list.data

export const useMonitorRecipes = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  return useQuery({
    queryFn: () => fetchMonitorRecipes(orgId, accessToken),
    queryKey: monitorsQueryKeys.recipes({ orgId }),
    staleTime: MONITOR_RECIPES_STALE_TIME_MS,
    enabled: !!orgId && !!accessToken,
    select: selectRecipeList,
  })
}

/** Index a recipe list by recipe key (pair with `useMonitorRecipes().data`). */
export const recipeByKey = (
  recipes: readonly CatalogRecipe[] | undefined,
): Map<string, CatalogRecipe> => {
  const map = new Map<string, CatalogRecipe>()
  for (const recipe of recipes ?? []) {
    map.set(recipe.key, recipe)
  }
  return map
}

// ─── Monitor list / detail ───────────────────────────────────────────────────

export const useOrganizationMonitors = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  return useQuery({
    queryFn: () => fetchOrganizationMonitors(orgId, accessToken),
    queryKey: monitorsQueryKeys.list({ orgId }),
    staleTime: MONITOR_OVERVIEW_REFETCH_INTERVAL_MS,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: MONITOR_OVERVIEW_REFETCH_INTERVAL_MS,
    enabled: !!orgId && !!accessToken,
  })
}

/**
 * Boolean "any unread monitor alert?" for the global sidebar dot. Shares the
 * org-monitors query KEY (so it rides the dashboard's existing fetch / cache —
 * no extra network), but `select`s down to a boolean so the always-mounted
 * sidebar re-renders only when the dot flips, not on every list change. Avoids
 * `useMonitorRows`' users+recipes fan-out, which the dot never needs.
 */
export const useHasMonitorUnread = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  return useQuery({
    queryFn: () => fetchOrganizationMonitors(orgId, accessToken),
    queryKey: monitorsQueryKeys.list({ orgId }),
    staleTime: MONITOR_OVERVIEW_REFETCH_INTERVAL_MS,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: MONITOR_OVERVIEW_REFETCH_INTERVAL_MS,
    enabled: !!orgId && !!accessToken,
    select: (data: MonitorListResponse) =>
      data.data.some((monitor) => (monitor.unread_count ?? 0) > 0),
  })
}

export const useMonitor = (monitorId: UUID | '', orgId: UUID | '') => {
  const { accessToken, loading: authIsLoading } = useAuthInfo()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryFn: () => fetchMonitor(monitorId, orgId, accessToken),
    queryKey: monitorsQueryKeys.detail({ monitorId }),
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!monitorId && !!orgId && !!accessToken,
    // Dashboard→workspace paints instantly: the org list cache already holds
    // the same overview shape for this monitor.
    placeholderData: () =>
      queryClient
        .getQueryData<MonitorListResponse>(monitorsQueryKeys.list({ orgId }))
        ?.data.find((monitor) => monitor.id === monitorId),
  })
  // Picked fields keep React Query's tracked-props optimization intact —
  // spreading the result would subscribe to every observer property.
  return {
    data: query.data,
    error: query.error,
    isLoading:
      authIsLoading || (!!monitorId && !accessToken) || query.isLoading,
  }
}

// ─── Dashboard / workspace view models ───────────────────────────────────────

export const useMonitorRows = (orgId: UUID | '') => {
  const { accessToken, loading: authIsLoading } = useAuthInfo()
  const monitorsQuery = useOrganizationMonitors(orgId)
  const recipesQuery = useMonitorRecipes(orgId)
  const { data: users, isLoading: usersIsLoading } = useOrganizationUsers({
    orgId,
  })

  const owners = useMemo(() => buildOwnerMap(users), [users])
  const recipesByKey = useMemo(
    () => recipeByKey(recipesQuery.data),
    [recipesQuery.data],
  )
  const rows = useMemo(
    () =>
      monitorsQuery.data?.data.map((monitor) =>
        toDashboardMonitor(monitor, owners, recipesByKey),
      ) ?? [],
    [monitorsQuery.data, owners, recipesByKey],
  )

  return {
    monitors: rows,
    isLoading:
      authIsLoading ||
      (!!orgId && !accessToken) ||
      monitorsQuery.isLoading ||
      usersIsLoading ||
      recipesQuery.isLoading,
    error: monitorsQuery.error,
    refetch: monitorsQuery.refetch,
  }
}

export const useMonitorRow = (monitorId: UUID | '', orgId: UUID | '') => {
  const monitorQuery = useMonitor(monitorId, orgId)
  const recipesQuery = useMonitorRecipes(orgId)
  const { data: users, isLoading: usersIsLoading } = useOrganizationUsers({
    orgId,
  })

  const owners = useMemo(() => buildOwnerMap(users), [users])
  const recipesByKey = useMemo(
    () => recipeByKey(recipesQuery.data),
    [recipesQuery.data],
  )
  const monitor = useMemo(
    () =>
      monitorQuery.data
        ? toDashboardMonitor(monitorQuery.data, owners, recipesByKey)
        : null,
    [monitorQuery.data, owners, recipesByKey],
  )

  return {
    monitor,
    rawMonitor: monitorQuery.data ?? null,
    isLoading:
      monitorQuery.isLoading || usersIsLoading || recipesQuery.isLoading,
    error: monitorQuery.error,
  }
}

// ─── Monitor mutations ───────────────────────────────────────────────────────

export const useCreateMonitor = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (req: MonitorCreate) => createMonitor(orgId, accessToken, req),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: monitorsQueryKeys.list({ orgId }),
      }),
  })
}

export const useUpdateMonitor = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ monitorId, req }: { monitorId: UUID; req: MonitorUpdate }) =>
      updateMonitor(monitorId, orgId, accessToken, req),
    onSuccess: (updated, { monitorId }) => {
      // The live PATCH returns plain MonitorOut — merge over the cached
      // overview so its optional overview-only fields survive.
      queryClient.setQueryData<MonitorOverviewOut | null>(
        monitorsQueryKeys.detail({ monitorId }),
        (previous) => (previous ? { ...previous, ...updated } : updated),
      )
      void queryClient.invalidateQueries({
        queryKey: monitorsQueryKeys.list({ orgId }),
      })
      // A rename invalidates the org-feed root too — cached entries embed the
      // monitor's name in grouped headers and row source pills, and the org
      // feed has no per-row patch path for that field.
      void queryClient.invalidateQueries({
        queryKey: monitorsQueryKeys.orgFeedRoot({ orgId }),
      })
    },
  })
}

export const useDeleteMonitor = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (monitorId: UUID) =>
      deleteMonitor(monitorId, orgId, accessToken),
    onSuccess: (_data, monitorId) => {
      queryClient.removeQueries({
        queryKey: monitorsQueryKeys.detail({ monitorId }),
      })
      void queryClient.invalidateQueries({
        queryKey: monitorsQueryKeys.list({ orgId }),
      })
      // The org-feed root and freshness peek can still hold the deleted
      // monitor's rows/counts — invalidate both so the master inbox drops them
      // instead of waiting out the org feed's own staleTime.
      void queryClient.invalidateQueries({
        queryKey: monitorsQueryKeys.orgFeedRoot({ orgId }),
      })
      void queryClient.invalidateQueries({
        queryKey: monitorsQueryKeys.orgFeedFreshnessRoot({ orgId }),
      })
    },
  })
}

// ─── Feed ────────────────────────────────────────────────────────────────────

const getNextOrgFeedPageParam = (
  lastPage: MonitorFeedListResponse,
  allPages: MonitorFeedListResponse[],
  lastPageParam: PaginateRequest,
): PaginateRequest | undefined => {
  if (!lastPage.has_more) return undefined
  return {
    limit: lastPageParam.limit,
    // Advance by the rows actually held across every cached page — robust to
    // optimistic row removal from any page, not just the last one.
    offset: allPages.reduce((total, page) => total + page.data.length, 0),
  }
}

// ─── Org-wide feed ───────────────────────────────────────────────────────────
// Server-filtered/sorted across every monitor in the org. Its cache lives
// under its own root (`orgFeedRoot`) so org-feed invalidations stay isolated
// from the rest of the monitor cache.

export const useOrgFeed = (orgId: UUID | '', query: OrgFeedQuery) => {
  const { accessToken, loading: authIsLoading } = useAuthInfo()
  const infinite = useInfiniteQuery({
    queryFn: ({ pageParam, signal }) =>
      fetchOrganizationFeed(orgId, accessToken, pageParam, query, signal),
    queryKey: monitorsQueryKeys.orgFeedList({ orgId, ...query }),
    initialPageParam: {
      limit: MONITOR_FEED_PAGE_SIZE,
      offset: 0,
    } satisfies PaginateRequest,
    // Advances offset by the rows actually held across cached pages (robust to
    // optimistic removal) and uses the provided lastPageParam for the page size.
    getNextPageParam: getNextOrgFeedPageParam,
    staleTime: 30_000,
    // The master inbox is PILL-DRIVEN once it has rows: arrivals surface via the
    // freshness peek + "↑ N new alerts" pill (useOrgFeedFreshness → refreshFeed),
    // never auto-injected into the scrolled/grouped visible list on focus — a
    // page-1 focus refetch of this event_at-DESC feed would re-bucket date groups
    // ("Today") and shift scroll.
    //
    // The ONE exception is an EMPTY loaded feed: with no rows there is nothing to
    // reorder or scroll, AND the freshness peek is gated off while empty
    // (feedEntries.length > 0) so it can't surface the first-ever arrival. So we
    // focus-refetch only while every loaded page is empty — cold-starting the
    // inbox — then hand off to the pill the moment any row exists.
    refetchOnWindowFocus: (q) =>
      (q.state.data?.pages ?? []).every((page) => page.data.length === 0),
    placeholderData: keepPreviousData,
    enabled: !!orgId && !!accessToken,
  })
  const feedEntries = useMemo(
    () => dedupeFeedEntries(infinite.data),
    [infinite.data],
  )
  return {
    feedEntries,
    statusCounts:
      infinite.data?.pages[0]?.status_counts ?? EMPTY_FEED_STATUS_COUNTS,
    // Server-computed per-kind facets (kind filter excluded), canonical on page 1.
    typeCounts: infinite.data?.pages[0]?.type_counts ?? EMPTY_FEED_TYPE_COUNTS,
    // Exact all-kinds total over the same (kind-excluded) scope as `typeCounts` —
    // unlike `count`, this never narrows when a specific kind filter is active,
    // so it's the correct source for an "All types" tally.
    typeCountsTotal: infinite.data?.pages[0]?.type_counts_total ?? 0,
    hasNextPage: infinite.hasNextPage,
    fetchNextPage: infinite.fetchNextPage,
    isFetchingNextPage: infinite.isFetchingNextPage,
    isLoading: authIsLoading || (!!orgId && !accessToken) || infinite.isLoading,
    error: infinite.error,
    refetch: infinite.refetch,
  }
}

// ─── Org-feed freshness peek ─────────────────────────────────────────────────
// Smart-polling liveness for the master inbox: a tiny dedicated query that
// fetches only the first page newest-by-time, so a caller can diff it against
// the loaded set to count genuinely-new arrivals (the "↑ N new alerts" pill).
//
// Why a SEPARATE query (its own `orgFeedFreshness` root, not the infinite
// `orgFeedRoot`): putting `refetchInterval` on the infinite query would refetch
// EVERY loaded page each tick (cost grows with scroll depth). The peek is a
// fixed cheap signal; the heavy infinite query refetches only on the user's
// explicit "load new" action.
//
// Always peeks with `sort: 'recent'` (event_at-DESC) regardless of the view's
// sort, because arrivals are detected by `event_at`. The view's status/
// monitorId/bookmarked/resourceKind/q facets are preserved so the peek is
// scoped to what the user is looking at — this is what keeps archived/foreign
// rows from surfacing as "new".
export const useOrgFeedFreshness = (
  orgId: UUID | '',
  query: OrgFeedQuery,
  enabled: boolean,
) => {
  const { accessToken } = useAuthInfo()
  const freshnessQuery: OrgFeedQuery = {
    ...query,
    sort: 'recent',
    resolution: 'notice',
  }
  return useQuery({
    queryFn: ({ signal }) =>
      fetchOrganizationFeed(
        orgId,
        accessToken,
        { limit: FEED_FRESHNESS_PEEK_SIZE, offset: 0 },
        freshnessQuery,
        signal,
      ),
    queryKey: monitorsQueryKeys.orgFeedFreshness({
      orgId,
      ...freshnessQuery,
    }),
    enabled: enabled && !!orgId && !!accessToken,
    refetchInterval: FEED_FRESHNESS_POLL_MS,
    // The infinite feed no longer focus-refetches (the inbox is pill-driven), so
    // this peek now OWNS focus-liveness: on tab-return it re-peeks page-1
    // newest-by-time so the "↑ N new alerts" pill is accurate immediately
    // instead of waiting up to FEED_FRESHNESS_POLL_MS. The peek only feeds the
    // count (diffed by countNewAlerts) and is never injected into the visible
    // list, so it surfaces arrivals without any reorder/scroll jank.
    refetchOnWindowFocus: true,
    // Pause the interval on hidden tabs; the focus refetch covers the gap on
    // return.
    refetchIntervalInBackground: false,
    // Always stale so the interval and mutation-driven invalidations refetch
    // without being skipped as fresh.
    staleTime: 0,
    select: selectFreshnessEntries,
  })
}

const selectFreshnessEntries = (
  page: MonitorFeedListResponse,
): MonitorFeedEntryOut[] => page.data

export const useMonitorFeedEntry = (
  monitorId: UUID | '',
  orgId: UUID | '',
  entryId: UUID | null,
) => {
  const { accessToken } = useAuthInfo()
  return useQuery({
    // skipToken already disables the query (and narrows the closure to
    // non-null args) when ids/token are missing — no redundant `enabled`.
    queryFn:
      monitorId && orgId && entryId && accessToken
        ? () => fetchMonitorFeedEntry(monitorId, orgId, accessToken, entryId)
        : skipToken,
    queryKey: monitorsQueryKeys.feedDetail({
      monitorId,
      entryId: entryId ?? '',
    }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
}

export const useMonitorFeedEntryLineage = (
  monitorId: UUID | '',
  orgId: UUID | '',
  entryId: UUID | null,
) => {
  const { accessToken } = useAuthInfo()
  return useQuery({
    // Fires in parallel with useMonitorFeedEntry on the same selection — both
    // are independent reads, so React Query runs them concurrently (no waterfall).
    queryFn:
      monitorId && orgId && entryId && accessToken
        ? () =>
            fetchMonitorFeedEntryLineage(monitorId, orgId, accessToken, entryId)
        : skipToken,
    queryKey: monitorsQueryKeys.feedLineage({
      monitorId,
      entryId: entryId ?? '',
    }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
}

// ─── Optimistic feed mutations ───────────────────────────────────────────────

// Snapshot of every cached infinite feed-list page under one root —
// `[queryKey, data]` pairs we restore on rollback.
type FeedListSnapshot = Array<
  [QueryKey, InfiniteData<MonitorFeedListResponse, PaginateRequest> | undefined]
>

type MonitorListSnapshot = Array<[QueryKey, MonitorListResponse | undefined]>

// ─── Org-feed optimistic mutations ───────────────────────────────────────────
// The master inbox spans monitors, so `monitorId` is supplied PER CALL (not
// bound at hook construction). Each mutation patches the org-feed root plus
// the monitor list/detail unread counts and feed detail — reusing the same
// pure helpers as the per-monitor mutations did. One optimistic patch + one
// invalidation set each.

interface OrgFeedEntryCacheContext {
  previousOrgFeedLists: FeedListSnapshot
  previousDetail: MonitorFeedEntryDetailOut | null | undefined
  previousMonitorLists: MonitorListSnapshot
  previousMonitorDetail: MonitorOverviewOut | null | undefined
  feedDetailKey: QueryKey
  monitorDetailKey: QueryKey
}

// Cancel + snapshot the org-feed root, the feed detail, and the monitor
// list/detail. Shared by every single-row org-feed mutation's onMutate.
const snapshotOrgFeedCaches = async (
  queryClient: QueryClient,
  orgId: UUID | '',
  monitorId: UUID,
  entryId: UUID,
): Promise<{
  orgFeedRootKey: QueryKey
  feedDetailKey: QueryKey
  monitorListsKey: QueryKey
  monitorDetailKey: QueryKey
  previousOrgFeedLists: FeedListSnapshot
  previousDetail: MonitorFeedEntryDetailOut | null | undefined
  previousMonitorLists: MonitorListSnapshot
  previousMonitorDetail: MonitorOverviewOut | null | undefined
}> => {
  const orgFeedRootKey = monitorsQueryKeys.orgFeedRoot({ orgId })
  const feedDetailKey = monitorsQueryKeys.feedDetail({ monitorId, entryId })
  const monitorListsKey = monitorsQueryKeys.lists()
  const monitorDetailKey = monitorsQueryKeys.detail({ monitorId })
  await Promise.all([
    queryClient.cancelQueries({ queryKey: orgFeedRootKey }),
    queryClient.cancelQueries({ queryKey: feedDetailKey }),
    queryClient.cancelQueries({ queryKey: monitorListsKey }),
    queryClient.cancelQueries({ queryKey: monitorDetailKey }),
  ])
  return {
    orgFeedRootKey,
    feedDetailKey,
    monitorListsKey,
    monitorDetailKey,
    previousOrgFeedLists: queryClient.getQueriesData<
      InfiniteData<MonitorFeedListResponse, PaginateRequest>
    >({ queryKey: orgFeedRootKey }),
    previousDetail: queryClient.getQueryData<MonitorFeedEntryDetailOut | null>(
      feedDetailKey,
    ),
    previousMonitorLists: queryClient.getQueriesData<MonitorListResponse>({
      queryKey: monitorListsKey,
    }),
    previousMonitorDetail: queryClient.getQueryData<MonitorOverviewOut | null>(
      monitorDetailKey,
    ),
  }
}

const rollbackOrgFeedEntryCaches = (
  queryClient: QueryClient,
  context: OrgFeedEntryCacheContext | undefined,
): void => {
  if (!context) return
  for (const [queryKey, data] of context.previousOrgFeedLists) {
    queryClient.setQueryData(queryKey, data)
  }
  queryClient.setQueryData(context.feedDetailKey, context.previousDetail)
  for (const [queryKey, data] of context.previousMonitorLists) {
    queryClient.setQueryData(queryKey, data)
  }
  queryClient.setQueryData(
    context.monitorDetailKey,
    context.previousMonitorDetail,
  )
}

// One invalidation per cache root touched: the org-feed root, the freshness
// peek root, every monitor list, the monitor detail, and this entry's detail.
// The freshness peek lives on its own namespace and the optimistic updaters
// never patch it, so it must be invalidated here — otherwise archiving/
// trashing a top-of-feed row leaves a stale arrival in the peek that reads as
// a false "N new alerts" until the next 60s poll.
const invalidateOrgFeedEntryQueries = (
  queryClient: QueryClient,
  orgId: UUID | '',
  monitorId: UUID,
  entryId: UUID,
): void => {
  void queryClient.invalidateQueries({
    queryKey: monitorsQueryKeys.orgFeedRoot({ orgId }),
  })
  void queryClient.invalidateQueries({
    queryKey: monitorsQueryKeys.orgFeedFreshnessRoot({ orgId }),
  })
  void queryClient.invalidateQueries({
    queryKey: monitorsQueryKeys.feedDetail({ monitorId, entryId }),
  })
  void queryClient.invalidateQueries({ queryKey: monitorsQueryKeys.lists() })
  void queryClient.invalidateQueries({
    queryKey: monitorsQueryKeys.detail({ monitorId }),
  })
}

export const useSetOrgFeedEntryRead = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      monitorId,
      entryId,
      read,
    }: {
      monitorId: UUID
      entryId: UUID
      read: boolean
    }) => setMonitorFeedEntryRead(monitorId, orgId, accessToken, entryId, read),
    onMutate: async ({ monitorId, entryId, read }) => {
      const snapshot = await snapshotOrgFeedCaches(
        queryClient,
        orgId,
        monitorId,
        entryId,
      )
      const unreadCountDelta = readStateUnreadCountDelta(
        findCachedFeedEntryReadState(
          snapshot.previousDetail,
          snapshot.previousOrgFeedLists,
          entryId,
        ),
        read,
      )
      // Read flips in place in every cached org-feed list (read never changes
      // list membership; lists only filter by status).
      for (const [queryKey] of snapshot.previousOrgFeedLists) {
        queryClient.setQueryData<
          InfiniteData<MonitorFeedListResponse, PaginateRequest>
        >(queryKey, (current) =>
          updateFeedEntriesReadPageData(current, entryId, read),
        )
      }
      queryClient.setQueryData<MonitorFeedEntryDetailOut | null>(
        snapshot.feedDetailKey,
        (current) => updateFeedEntryDetailRead(current, read),
      )
      queryClient.setQueriesData<MonitorListResponse>(
        { queryKey: snapshot.monitorListsKey },
        (current) =>
          updateMonitorListUnreadCount(current, monitorId, unreadCountDelta),
      )
      queryClient.setQueryData<MonitorOverviewOut | null>(
        snapshot.monitorDetailKey,
        (current) =>
          updateMonitorUnreadCount(current, monitorId, unreadCountDelta),
      )
      return {
        previousOrgFeedLists: snapshot.previousOrgFeedLists,
        previousDetail: snapshot.previousDetail,
        previousMonitorLists: snapshot.previousMonitorLists,
        previousMonitorDetail: snapshot.previousMonitorDetail,
        feedDetailKey: snapshot.feedDetailKey,
        monitorDetailKey: snapshot.monitorDetailKey,
      } satisfies OrgFeedEntryCacheContext
    },
    onError: (_err, _vars, context) => {
      rollbackOrgFeedEntryCaches(queryClient, context)
    },
    onSettled: (_data, _error, vars) => {
      if (!vars) return
      invalidateOrgFeedEntryQueries(
        queryClient,
        orgId,
        vars.monitorId,
        vars.entryId,
      )
    },
  })
}

// Whether a moved row should drop out of a given cached list under the org-feed
// status move. Only status-scoped lists evict (a `bookmarked=true` Starred view
// is orthogonal to status and keeps the row); a list with no status filter keeps
// everything non-trashed, so it evicts only on a move to trashed.
const orgFeedStatusEvicts = (
  filter: OrgFeedListFilter | null,
  nextStatus: MonitorFeedEntryStatus,
): boolean => {
  if (!filter) return false
  if (filter.status) return filter.status !== nextStatus
  // No status filter ⇒ shows inbox+archived (non-trashed); evict on trash.
  return nextStatus === 'trashed'
}

export const useSetOrgFeedEntryStatus = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      monitorId,
      entryId,
      status,
    }: {
      monitorId: UUID
      entryId: UUID
      status: MonitorFeedEntryStatus
    }) =>
      updateMonitorFeedEntryStatus(
        monitorId,
        orgId,
        accessToken,
        entryId,
        status,
      ),
    onMutate: async ({ monitorId, entryId, status }) => {
      const snapshot = await snapshotOrgFeedCaches(
        queryClient,
        orgId,
        monitorId,
        entryId,
      )
      const movedEntry =
        (snapshot.previousDetail?.id === entryId
          ? snapshot.previousDetail
          : undefined) ??
        snapshot.previousOrgFeedLists
          .flatMap(([, data]) => data?.pages ?? [])
          .flatMap((page) => page.data)
          .find((entry) => entry.id === entryId)
      const unreadCountDelta = statusChangeUnreadCountDelta(movedEntry, status)

      // Org-feed lists evict by the org filter (a Starred view keeps the row);
      // synthesize a per-list MonitorFeedListFilter for the page-data helper.
      for (const [queryKey] of snapshot.previousOrgFeedLists) {
        const orgFilter = orgFeedListFilterFromKey(queryKey)
        const evicts = orgFeedStatusEvicts(orgFilter, status)
        queryClient.setQueryData<
          InfiniteData<MonitorFeedListResponse, PaginateRequest>
        >(queryKey, (current) =>
          updateFeedEntriesPageData(
            current,
            entryId,
            status,
            movedEntry?.status,
            evicts ? { status: movedEntry?.status } : {},
          ),
        )
      }
      queryClient.setQueryData<MonitorFeedEntryDetailOut | null>(
        snapshot.feedDetailKey,
        (current) => updateFeedEntryDetail(current, status),
      )
      queryClient.setQueriesData<MonitorListResponse>(
        { queryKey: snapshot.monitorListsKey },
        (current) =>
          updateMonitorListUnreadCount(current, monitorId, unreadCountDelta),
      )
      queryClient.setQueryData<MonitorOverviewOut | null>(
        snapshot.monitorDetailKey,
        (current) =>
          updateMonitorUnreadCount(current, monitorId, unreadCountDelta),
      )
      return {
        previousOrgFeedLists: snapshot.previousOrgFeedLists,
        previousDetail: snapshot.previousDetail,
        previousMonitorLists: snapshot.previousMonitorLists,
        previousMonitorDetail: snapshot.previousMonitorDetail,
        feedDetailKey: snapshot.feedDetailKey,
        monitorDetailKey: snapshot.monitorDetailKey,
      } satisfies OrgFeedEntryCacheContext
    },
    onError: (_err, _vars, context) => {
      rollbackOrgFeedEntryCaches(queryClient, context)
    },
    onSettled: (_data, _error, vars) => {
      if (!vars) return
      invalidateOrgFeedEntryQueries(
        queryClient,
        orgId,
        vars.monitorId,
        vars.entryId,
      )
    },
  })
}

export const useToggleOrgFeedEntryBookmark = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      monitorId,
      entryId,
      bookmarked,
    }: {
      monitorId: UUID
      entryId: UUID
      bookmarked: boolean
    }) =>
      setMonitorFeedEntryBookmarked(
        monitorId,
        orgId,
        accessToken,
        entryId,
        bookmarked,
      ),
    onMutate: async ({ monitorId, entryId, bookmarked }) => {
      const orgFeedRootKey = monitorsQueryKeys.orgFeedRoot({ orgId })
      const feedDetailKey = monitorsQueryKeys.feedDetail({ monitorId, entryId })
      await Promise.all([
        queryClient.cancelQueries({ queryKey: orgFeedRootKey }),
        queryClient.cancelQueries({ queryKey: feedDetailKey }),
      ])
      const previousOrgFeedLists = queryClient.getQueriesData<
        InfiniteData<MonitorFeedListResponse, PaginateRequest>
      >({ queryKey: orgFeedRootKey })
      const previousDetail =
        queryClient.getQueryData<MonitorFeedEntryDetailOut | null>(
          feedDetailKey,
        )
      // The star flips in place across every cached org-feed list.
      // Bookmarked-only lists drop unstarred rows immediately; the settled
      // refetch reconciles any cached list that did not already contain the row.
      for (const [queryKey] of previousOrgFeedLists) {
        queryClient.setQueryData<
          InfiniteData<MonitorFeedListResponse, PaginateRequest>
        >(queryKey, (current) =>
          updateFeedEntriesBookmarkedPageData(
            current,
            entryId,
            bookmarked,
            orgFeedListFilterFromKey(queryKey) ?? {},
          ),
        )
      }
      queryClient.setQueryData<MonitorFeedEntryDetailOut | null>(
        feedDetailKey,
        (current) => updateFeedEntryDetailBookmarked(current, bookmarked),
      )
      return {
        previousOrgFeedLists,
        previousDetail,
        feedDetailKey,
      }
    },
    onError: (_err, _vars, context) => {
      if (!context) return
      for (const [queryKey, data] of context.previousOrgFeedLists) {
        queryClient.setQueryData(queryKey, data)
      }
      queryClient.setQueryData(context.feedDetailKey, context.previousDetail)
    },
    onSettled: (_data, _error, vars) => {
      if (!vars) return
      void queryClient.invalidateQueries({
        queryKey: monitorsQueryKeys.orgFeedRoot({ orgId }),
      })
      // Re-sync the freshness peek: in the Starred folder (bookmarked=true) an
      // un-star evicts the row from the loaded set, so the peek must drop it too
      // or it reads as a false "new alert" (see invalidateOrgFeedEntryQueries).
      void queryClient.invalidateQueries({
        queryKey: monitorsQueryKeys.orgFeedFreshnessRoot({ orgId }),
      })
      void queryClient.invalidateQueries({
        queryKey: monitorsQueryKeys.feedDetail({
          monitorId: vars.monitorId,
          entryId: vars.entryId,
        }),
      })
    },
  })
}

// ─── Org-feed optimistic batch mutations ─────────────────────────────────────
// Bulk handlers GROUP the checked set by monitor_id and call these once per
// monitor (fan-out via Promise.allSettled in the view-state hook). Each call
// patches the org-feed root in ONE pass over the requested ids, and invalidates
// ONCE per root in onSettled.

interface OrgFeedBatchCacheContext {
  previousOrgFeedLists: FeedListSnapshot
  previousMonitorLists: MonitorListSnapshot
  previousMonitorDetail: MonitorOverviewOut | null | undefined
  monitorDetailKey: QueryKey
}

const rollbackOrgFeedBatchCaches = (
  queryClient: QueryClient,
  context: OrgFeedBatchCacheContext | undefined,
): void => {
  if (!context) return
  for (const [queryKey, data] of context.previousOrgFeedLists) {
    queryClient.setQueryData(queryKey, data)
  }
  for (const [queryKey, data] of context.previousMonitorLists) {
    queryClient.setQueryData(queryKey, data)
  }
  queryClient.setQueryData(
    context.monitorDetailKey,
    context.previousMonitorDetail,
  )
}

const invalidateOrgFeedBatchQueries = (
  queryClient: QueryClient,
  orgId: UUID | '',
  monitorId: UUID,
): void => {
  void queryClient.invalidateQueries({
    queryKey: monitorsQueryKeys.orgFeedRoot({ orgId }),
  })
  // Re-sync the freshness peek (see invalidateOrgFeedEntryQueries) — a bulk
  // archive of top-of-feed rows would otherwise leave a stale false count.
  void queryClient.invalidateQueries({
    queryKey: monitorsQueryKeys.orgFeedFreshnessRoot({ orgId }),
  })
  void queryClient.invalidateQueries({ queryKey: monitorsQueryKeys.lists() })
  void queryClient.invalidateQueries({
    queryKey: monitorsQueryKeys.detail({ monitorId }),
  })
}

export const useBatchSetOrgFeedEntryRead = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      monitorId,
      entryIds,
      read,
    }: {
      monitorId: UUID
      entryIds: UUID[]
      read: boolean
    }) =>
      batchSetMonitorFeedEntryRead(
        monitorId,
        orgId,
        accessToken,
        entryIds,
        read,
      ),
    onMutate: async ({ monitorId, entryIds, read }) => {
      const orgFeedRootKey = monitorsQueryKeys.orgFeedRoot({ orgId })
      const monitorListsKey = monitorsQueryKeys.lists()
      const monitorDetailKey = monitorsQueryKeys.detail({ monitorId })
      await Promise.all([
        queryClient.cancelQueries({ queryKey: orgFeedRootKey }),
        queryClient.cancelQueries({ queryKey: monitorListsKey }),
        queryClient.cancelQueries({ queryKey: monitorDetailKey }),
      ])
      const previousOrgFeedLists = queryClient.getQueriesData<
        InfiniteData<MonitorFeedListResponse, PaginateRequest>
      >({ queryKey: orgFeedRootKey })
      const previousMonitorLists =
        queryClient.getQueriesData<MonitorListResponse>({
          queryKey: monitorListsKey,
        })
      const previousMonitorDetail =
        queryClient.getQueryData<MonitorOverviewOut | null>(monitorDetailKey)

      let unreadCountDelta = 0
      for (const entryId of entryIds) {
        unreadCountDelta += readStateUnreadCountDelta(
          findCachedFeedEntryReadState(
            undefined,
            previousOrgFeedLists,
            entryId,
          ),
          read,
        )
      }

      const entryIdSet = new Set(entryIds)
      for (const [queryKey] of previousOrgFeedLists) {
        queryClient.setQueryData<
          InfiniteData<MonitorFeedListResponse, PaginateRequest>
        >(queryKey, (current) => {
          let next = current
          for (const entryId of entryIdSet) {
            next = updateFeedEntriesReadPageData(next, entryId, read)
          }
          return next
        })
      }
      queryClient.setQueriesData<MonitorListResponse>(
        { queryKey: monitorListsKey },
        (current) =>
          updateMonitorListUnreadCount(current, monitorId, unreadCountDelta),
      )
      queryClient.setQueryData<MonitorOverviewOut | null>(
        monitorDetailKey,
        (current) =>
          updateMonitorUnreadCount(current, monitorId, unreadCountDelta),
      )
      return {
        previousOrgFeedLists,
        previousMonitorLists,
        previousMonitorDetail,
        monitorDetailKey,
      } satisfies OrgFeedBatchCacheContext
    },
    onError: (_err, _vars, context) => {
      rollbackOrgFeedBatchCaches(queryClient, context)
    },
    onSettled: (_data, _error, vars) => {
      if (!vars) return
      invalidateOrgFeedBatchQueries(queryClient, orgId, vars.monitorId)
    },
  })
}

export const useBatchSetOrgFeedEntryStatus = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      monitorId,
      entryIds,
      status,
    }: {
      monitorId: UUID
      entryIds: UUID[]
      status: MonitorFeedEntryStatus
    }) =>
      batchSetMonitorFeedEntryStatus(
        monitorId,
        orgId,
        accessToken,
        entryIds,
        status,
      ),
    onMutate: async ({ monitorId, entryIds, status }) => {
      const orgFeedRootKey = monitorsQueryKeys.orgFeedRoot({ orgId })
      const monitorListsKey = monitorsQueryKeys.lists()
      const monitorDetailKey = monitorsQueryKeys.detail({ monitorId })
      await Promise.all([
        queryClient.cancelQueries({ queryKey: orgFeedRootKey }),
        queryClient.cancelQueries({ queryKey: monitorListsKey }),
        queryClient.cancelQueries({ queryKey: monitorDetailKey }),
      ])
      const previousOrgFeedLists = queryClient.getQueriesData<
        InfiniteData<MonitorFeedListResponse, PaginateRequest>
      >({ queryKey: orgFeedRootKey })
      const previousMonitorLists =
        queryClient.getQueriesData<MonitorListResponse>({
          queryKey: monitorListsKey,
        })
      const previousMonitorDetail =
        queryClient.getQueryData<MonitorOverviewOut | null>(monitorDetailKey)

      const entryIdSet = new Set(entryIds)
      const movedEntries = new Map<UUID, MonitorFeedEntryOut>()
      for (const entry of previousOrgFeedLists
        .flatMap(([, data]) => data?.pages ?? [])
        .flatMap((page) => page.data)) {
        if (entryIdSet.has(entry.id) && !movedEntries.has(entry.id)) {
          movedEntries.set(entry.id, entry)
        }
      }

      let unreadCountDelta = 0
      for (const entry of movedEntries.values()) {
        unreadCountDelta += statusChangeUnreadCountDelta(entry, status)
      }

      // Org-feed lists evict by the org filter.
      for (const [queryKey] of previousOrgFeedLists) {
        const evicts = orgFeedStatusEvicts(
          orgFeedListFilterFromKey(queryKey),
          status,
        )
        queryClient.setQueryData<
          InfiniteData<MonitorFeedListResponse, PaginateRequest>
        >(queryKey, (current) => {
          let next = current
          for (const entryId of entryIdSet) {
            next = updateFeedEntriesPageData(
              next,
              entryId,
              status,
              movedEntries.get(entryId)?.status,
              evicts ? { status: movedEntries.get(entryId)?.status } : {},
            )
          }
          return next
        })
      }
      queryClient.setQueriesData<MonitorListResponse>(
        { queryKey: monitorListsKey },
        (current) =>
          updateMonitorListUnreadCount(current, monitorId, unreadCountDelta),
      )
      queryClient.setQueryData<MonitorOverviewOut | null>(
        monitorDetailKey,
        (current) =>
          updateMonitorUnreadCount(current, monitorId, unreadCountDelta),
      )
      return {
        previousOrgFeedLists,
        previousMonitorLists,
        previousMonitorDetail,
        monitorDetailKey,
      } satisfies OrgFeedBatchCacheContext
    },
    onError: (_err, _vars, context) => {
      rollbackOrgFeedBatchCaches(queryClient, context)
    },
    onSettled: (_data, _error, vars) => {
      if (!vars) return
      invalidateOrgFeedBatchQueries(queryClient, orgId, vars.monitorId)
    },
  })
}

// ─── Monitor Slack channels (live) ───────────────────────────────────────────

export const useMonitorSlackChannels = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  return useQuery({
    queryFn: () => fetchMonitorSlackChannels(orgId, accessToken),
    queryKey: monitorsQueryKeys.slackChannels({ orgId }),
    staleTime: 60_000,
    enabled: !!orgId && !!accessToken,
  })
}

export const useAddMonitorSlackChannel = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (req: MonitorSlackChannelCreate) =>
      createMonitorSlackChannel(orgId, accessToken, req),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: monitorsQueryKeys.slackChannels({ orgId }),
      }),
  })
}

export const useRemoveMonitorSlackChannel = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (channelId: UUID) =>
      deleteMonitorSlackChannel(orgId, accessToken, channelId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: monitorsQueryKeys.slackChannels({ orgId }),
      }),
  })
}
